import React, { type ReactNode } from "react";
import { type Theme } from "contexts/color-theme";
import { AspectRatio, Text, Tab, type TabProps } from "@chakra-ui/react";
import Image from "next/image";

export type FilterTabProps = {
  /**
   * Indicates if the tab is active.
   *
   * @default false
   */
  isActive?: boolean;

  /**
   * The tab label.
   */
  label: string;

  /**
   * Logo to display in the tab list. Provide an image URL from Sanity.
   *
   * Required if FilterTabs `variant` is set to "logo".
   */
  logo?: string;

  /**
   * The tab content.
   */
  items: ReactNode;

  /**
   * The height of the tab.
   */
  height?: TabProps["height"];

  /**
   * The color theme of the tab list.
   *
   * @default "light"
   */
  theme?: Theme;
};

export function FilterTab({ isActive, height, label, logo, theme }: FilterTabProps) {
  const inactiveLogoFilter = theme === "light" ? "brightness(0)" : "brightness(0) invert(1)";
  const activeLogoFilter = theme === "light" ? "brightness(0) invert(1)" : "brightness(0)";
  const inactiveOpacity = theme === "light" ? 0.6 : 1;

  return (
    <Tab
      key={label}
      color={isActive ? `brand.backgroundSecondary.${theme}` : `brand.text.${theme}`}
      fontWeight="semibold"
      height={height}
      px={{ base: "smallest", md: "smaller" }}
      zIndex={1} // Required so the tab content sits above the `TabIndicator`
    >
      {logo ? (
        <AspectRatio
          ratio={92 / 28}
          height={{ base: "24px", md: "28px" }}
          width={{ base: "70px", md: "92px" }}
          pos="relative"
          filter={isActive ? activeLogoFilter : inactiveLogoFilter}
          opacity={isActive ? 1 : inactiveOpacity}
        >
          <Image fill sizes="100%" src={`${logo}?w=184`} alt={label} style={{ objectFit: "contain" }} />
        </AspectRatio>
      ) : (
        <Text as="span" textStyle={{ base: "caption2", md: "caption1" }}>
          {label}
        </Text>
      )}
    </Tab>
  );
}
